import { sdkRequest } from '@eventbrite/http';
import { Nullable } from '@eventbrite/ts-utils';
import { CollectionResponse, CollectionShape } from '../types';

interface ImageServiceResponse {
    edge_color_set: boolean;
    edge_color: Nullable<string>;
    url: string;
    original: {
        url: string;
        width: number;
        height: number;
    };
    crop_mask: Nullable<string>;
    aspect_ratio: string;
    id: string;
}

export const getImage = async (imageId: string) => {
    try {
        const response: ImageServiceResponse = await sdkRequest(
            `/api/v3/media/${imageId}/`,
        );
        return response.url;
    } catch (e) {
        console.warn('Error fetching image', e);
        return '';
    }
};

const getCollectionsForPlaces = async (places: string[]) => {
    const collections: CollectionResponse[] = await Promise.all(
        places.map(async (placeId) => {
            return getCollectionsForPlaceId(placeId, 5);
        }),
    );

    //merge into object by ID to dedupe collections
    const mergedCollections = collections.reduce<{
        [collectionId: string]: CollectionShape;
    }>((memo, { collections: collectionList }) => {
        collectionList.map((collection) => (memo[collection.id] = collection));
        return memo;
    }, {});

    //create list of collections
    const dedupedCollections = Object.keys(mergedCollections).map(
        (id) => mergedCollections[id],
    );

    return { collections: dedupedCollections };
};

const getCollectionsForPlaceId = async (
    placeId: string,
    pageSize = 10,
): Promise<CollectionResponse> =>
    sdkRequest(
        `/api/v3/destination/collections/${placeId}/editorial-collections/?expand.collection=image_large&page_size=${pageSize}`,
    );

const getCollectionsForOnline = async (): Promise<CollectionResponse> =>
    sdkRequest(
        '/api/v3/destination/collections/?is_online=true&expand.collection=image_large&page_size=10',
    );

export const getCollectionsForPlace = async (place: string | string[]) => {
    let collections: CollectionResponse;

    if (Array.isArray(place)) {
        collections = await getCollectionsForPlaces(place);
    } else if (place === 'online') {
        collections = await getCollectionsForOnline();
    } else {
        collections = await getCollectionsForPlaceId(place);
    }

    return collections;
};
